import React, { useEffect, useState } from "react";
import { 
  GetDataFromApiWithParams,
  UpdateAndGetResponse,
  DeleteAndGetResponse,
  ExecutePostWithParams
} from "../../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Container,
  Divider
} from "@mui/material";
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon
} from "@mui/icons-material";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import { IPDCToastMessageResult } from "../../../../../components/Controls";

// EditableTableCell component for inline editing
const EditableTableCell = ({ record, field, value, isEditing, onFieldChange, fieldType = "text", options }) => {
  if (isEditing) {
    // For date fields
    if (fieldType === "date") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: '100%' }}
          />
        </TableCell>
      );
    }
    
    // For numeric fields
    if (fieldType === "number") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={value || 0}
            onChange={(e) => onFieldChange(field, parseFloat(e.target.value) || 0)}
            sx={{ width: '120px' }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </TableCell>
      );
    }
    
    // For text fields
    if (fieldType === "text") {
      return (
        <TableCell>
          <TextField
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '100%' }}
            multiline={field === "description" || field === "arrear_skipping_description"}
            rows={field === "description" || field === "arrear_skipping_description" ? 2 : 1}
          />
        </TableCell>
      );
    }
    
    // For select fields
    if (fieldType === "select" && options) {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '150px' }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
      );
    }
    
    // For payment mode select
    if (fieldType === "paymentMode") {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '150px' }}
          >
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Cheque">Cheque</MenuItem>
            <MenuItem value="UPI">UPI</MenuItem>
          </Select>
        </TableCell>
      );
    }

    // For payment type select
    if (fieldType === "paymentType") {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '150px' }}
          >
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Cheque">Cheque</MenuItem>
            <MenuItem value="UPI">UPI</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
            <MenuItem value="Credit Card">Credit Card</MenuItem>
          </Select>
        </TableCell>
      );
    }
    
    // For status field (select)
    if (fieldType === "status") {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || 'pending'}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '120px' }}
          >
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="requested">Requested</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </TableCell>
      );
    }
    
    // Default to regular cell for non-editable fields
    return <TableCell>{value}</TableCell>;
  }
  
  // Non-editable mode - display values
  if (fieldType === "date" && value) {
    return <TableCell>{new Date(value).toLocaleDateString()}</TableCell>;
  }
  
  if (fieldType === "number" && value) {
    return <TableCell>${parseFloat(value).toFixed(2)}</TableCell>;
  }
  
  return <TableCell>{value || ''}</TableCell>;
};

export default function ApproveAdvancePayment() {
  // ==================== STATE DECLARATIONS ====================
  
  // Main states for advance payments
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Main states for periodic payments
  const [periodicPaymentData, setPeriodicPaymentData] = useState([]);
  const [periodicLoading, setPeriodicLoading] = useState(false);
  
  // Search and filter states for advance payments
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Search and filter states for periodic payments
  const [periodicSearchQuery, setPeriodicSearchQuery] = useState("");
  const [periodicSearchResults, setPeriodicSearchResults] = useState([]);
  const [isPeriodicSearching, setIsPeriodicSearching] = useState(false);
  
  // Status filter states for advance payments
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  
  // Status filter states for periodic payments
  const [periodicStatusFilter, setPeriodicStatusFilter] = useState("all");
  const [periodicFilterAnchorEl, setPeriodicFilterAnchorEl] = useState(null);
  
  // Row selection and action states for advance payments
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  
  // Row selection and action states for periodic payments
  const [selectedPeriodicRow, setSelectedPeriodicRow] = useState(null);
  const [periodicActionMenuAnchorEl, setPeriodicActionMenuAnchorEl] = useState(null);
  
  // Dialog states for advance payments
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Dialog states for periodic payments
  const [periodicApprovalDialogOpen, setPeriodicApprovalDialogOpen] = useState(false);
  const [periodicDeclineDialogOpen, setPeriodicDeclineDialogOpen] = useState(false);
  const [periodicRequestDialogOpen, setPeriodicRequestDialogOpen] = useState(false);
  const [periodicViewDialogOpen, setPeriodicViewDialogOpen] = useState(false);
  
  const [remarks, setRemarks] = useState("");
  
  // Processing states
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Inline editing states for advance payments
  const [editingId, setEditingId] = useState(null);
  const [editedRowData, setEditedRowData] = useState(null);
  
  // Inline editing states for periodic payments
  const [periodicEditingInvoiceNo, setPeriodicEditingInvoiceNo] = useState(null);
  const [editedPeriodicRowData, setEditedPeriodicRowData] = useState(null);
  
  // Pagination states for advance payments
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Pagination states for periodic payments
  const [periodicPage, setPeriodicPage] = useState(0);
  const [periodicRowsPerPage, setPeriodicRowsPerPage] = useState(10);
  const [periodicTotalRecords, setPeriodicTotalRecords] = useState(0);
  
  // Toast Notification State
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  
  const decodedUser = useDecodedUser();
  
  // ==================== TOAST FUNCTIONS ====================
  
  const showToast = (message, type = "success") => {
    setToastMessage({ message, type });
  };

  const showSuccessToast = (message) => {
    showToast(message, "success");
  };

  const showErrorToast = (message) => {
    showToast(message, "error");
  };

  const showWarningToast = (message) => {
    showToast(message, "warning");
  };

  // ==================== UTILITY FUNCTIONS ====================

  // Get status color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case "approved": return "success";
      case "declined": return "error";
      case "requested": return "info";
      case "pending": return "warning";
      default: return "default";
    }
  };

  // Get chip color
  const getChipColor = (status) => {
    switch(status?.toLowerCase()) {
      case "approved": return "success";
      case "declined": return "error";
      case "requested": return "info";
      case "pending": return "warning";
      default: return "default";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // ==================== FETCH FUNCTIONS ====================

  // Fetch advance payment data
  const fetchAdvancePayments = async () => {
    setLoading(true);
    try {
      const response = await GetDataFromApiWithParams("finances/paymentsearch");
      console.log("SEARCH RESULT", response)
      if (response && Array.isArray(response)) {
        console.log("Advance payment data fetched:", response);
        
        // Format dates for display
        const formattedData = response.map((item) => ({
          ...item,
          payment_date_formatted: formatDate(item.payment_date),
          requested_date_formatted: formatDate(item.requested_date),
          payment_amount_formatted: formatCurrency(item.payment_amount),
          status: item.status || "pending"
        }));
        
        setPaymentData(formattedData);
        setSearchResults(formattedData);
        setTotalRecords(formattedData.length);
      } else {
        setPaymentData([]);
        setSearchResults([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching advance payment data:", error);
      showErrorToast("Failed to load advance payment records");
    } finally {
      setLoading(false);
    }
  };

  // Fetch periodic payment data from fin_payment table
  const fetchPeriodicPayments = async () => {
    setPeriodicLoading(true);
    try {
      const response = await GetDataFromApiWithParams("finances/periodicpayments");
      console.log("Periodic payment data fetched:", response);
      
      if (response && Array.isArray(response)) {
        // Format data based on fin_payment structure - NO ID FIELD
        const formattedData = response.map((item) => ({
          // Use invoice_no as the unique identifier
          invoice_no: item.invoice_no,
          agreement_id: item.agreement_id,
          amount: item.amount,
          payment_date: item.payment_date,
          payment_type: item.payment_type,
          bank_name: item.bank_name,
          reference_no: item.reference_no,
          description: item.description,
          if_late_penalty_interest_amount: item.if_late_penality_interest_amount || 0,
          recieved_by: item.recieved_by,
          payment_period: item.payment_period,
          arrear_skipped_amount: item.arrear_skipped_amount || 0,
          arrear_skipping_description: item.arrear_skipping_description,
          references_path: item.references_path,
          status: item.status || "pending",
          // Formatted fields
          payment_date_formatted: formatDate(item.payment_date),
          amount_formatted: formatCurrency(item.amount),
          late_interest_formatted: formatCurrency(item.if_late_penality_interest_amount),
          arrear_skipped_formatted: formatCurrency(item.arrear_skipped_amount)
        }));
        
        setPeriodicPaymentData(formattedData);
        setPeriodicSearchResults(formattedData);
        setPeriodicTotalRecords(formattedData.length);
      }
    } catch (error) {
      console.error("Error fetching periodic payment data:", error);
      showErrorToast("Failed to load periodic payment records");
    } finally {
      setPeriodicLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvancePayments();
    fetchPeriodicPayments();
  }, []);

  // ==================== SEARCH FUNCTIONS ====================

  // Search function for advance payments
  const handleSearch = async () => {
    try {
      setIsSearching(true);
      
      if (!searchQuery.trim()) {
        setSearchResults(paymentData);
        setTotalRecords(paymentData.length);
        return;
      }
      
      const searchValue = searchQuery.toLowerCase().trim();
      const filtered = paymentData.filter(item => 
        (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
        (item.reference_no && item.reference_no.toLowerCase().includes(searchValue)) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchValue)) ||
        (item.payment_mode && item.payment_mode.toLowerCase().includes(searchValue))
      );
      
      setSearchResults(filtered);
      setTotalRecords(filtered.length);
      setPage(0);
      
      if (filtered.length > 0) {
        showSuccessToast(`Found ${filtered.length} advance payment records!`);
      } else {
        showWarningToast("No records found for your search.");
      }
    } catch (error) {
      console.error("Error searching advance payments:", error);
      showErrorToast("Search failed! Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  // Search function for periodic payments
  const handlePeriodicSearch = async () => {
    try {
      setIsPeriodicSearching(true);
      
      if (!periodicSearchQuery.trim()) {
        setPeriodicSearchResults(periodicPaymentData);
        setPeriodicTotalRecords(periodicPaymentData.length);
        return;
      }
      
      const searchValue = periodicSearchQuery.toLowerCase().trim();
      const filtered = periodicPaymentData.filter(item => 
        (item.invoice_no && item.invoice_no.toString().toLowerCase().includes(searchValue)) ||
        (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
        (item.reference_no && item.reference_no.toLowerCase().includes(searchValue)) ||
        (item.description && item.description.toLowerCase().includes(searchValue)) ||
        (item.bank_name && item.bank_name.toLowerCase().includes(searchValue)) ||
        (item.payment_period && item.payment_period.toLowerCase().includes(searchValue))
      );
      
      setPeriodicSearchResults(filtered);
      setPeriodicTotalRecords(filtered.length);
      setPeriodicPage(0);
      
      if (filtered.length > 0) {
        showSuccessToast(`Found ${filtered.length} periodic payment records!`);
      } else {
        showWarningToast("No periodic payment records found for your search.");
      }
    } catch (error) {
      console.error("Error searching periodic payments:", error);
      showErrorToast("Search failed! Please try again.");
    } finally {
      setIsPeriodicSearching(false);
    }
  };

  // Handle enter key in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePeriodicSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePeriodicSearch();
    }
  };

  // ==================== FILTER FUNCTIONS ====================

  // Apply status filters for advance payments
  const applyStatusFilter = (status) => {
    setStatusFilter(status);
    let filtered = [...paymentData];
    
    if (status !== "all") {
      filtered = filtered.filter(item => 
        (item.status || "pending").toLowerCase() === status.toLowerCase()
      );
    }
    
    if (searchQuery.trim()) {
      const searchValue = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
        (item.reference_no && item.reference_no.toLowerCase().includes(searchValue)) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchValue))
      );
    }
    
    setSearchResults(filtered);
    setTotalRecords(filtered.length);
    setPage(0);
    setFilterAnchorEl(null);
  };

  // Apply status filters for periodic payments
  const applyPeriodicStatusFilter = (status) => {
    setPeriodicStatusFilter(status);
    let filtered = [...periodicPaymentData];
    
    if (status !== "all") {
      filtered = filtered.filter(item => 
        (item.status || "pending").toLowerCase() === status.toLowerCase()
      );
    }
    
    if (periodicSearchQuery.trim()) {
      const searchValue = periodicSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.invoice_no && item.invoice_no.toString().toLowerCase().includes(searchValue)) ||
        (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
        (item.reference_no && item.reference_no.toLowerCase().includes(searchValue))
      );
    }
    
    setPeriodicSearchResults(filtered);
    setPeriodicTotalRecords(filtered.length);
    setPeriodicPage(0);
    setPeriodicFilterAnchorEl(null);
  };

  // Clear all filters for advance payments
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    fetchAdvancePayments();
    showWarningToast("Advance payment filters cleared");
  };

  // Clear all filters for periodic payments
  const handlePeriodicClearFilters = () => {
    setPeriodicSearchQuery("");
    setPeriodicStatusFilter("all");
    fetchPeriodicPayments();
    showWarningToast("Periodic payment filters cleared");
  };

  // ==================== INLINE EDITING FUNCTIONS ====================

  // Start inline editing for advance payments
  const handleInlineEdit = (record) => {
    if (editingId && editingId !== record.id) {
      handleCancelInlineEdit();
    }
    
    setEditingId(record.id);
    setEditedRowData({...record});
  };

  // Start inline editing for periodic payments
  const handlePeriodicInlineEdit = (record) => {
    if (periodicEditingInvoiceNo && periodicEditingInvoiceNo !== record.invoice_no) {
      handlePeriodicCancelInlineEdit();
    }
    
    setPeriodicEditingInvoiceNo(record.invoice_no);
    setEditedPeriodicRowData({...record});
  };

  // Save inline edits for advance payments
  const handleSaveInlineEdit = async () => {
    if (!editedRowData) return;
    
    try {
      setIsProcessing(true);
      
      const updateData = {
        ...editedRowData,
        payment_date: editedRowData.payment_date,
        payment_date_formatted: undefined,
        requested_date_formatted: undefined,
        payment_amount_formatted: undefined
      };
      
      console.log("Updating advance record:", editingId, updateData);
      
      const result = await UpdateAndGetResponse(`finances/advance-payments/${editingId}`, updateData);
      
      if (result) {
        setSearchResults(prevResults => 
          prevResults.map(record => 
            record.id === editingId ? { ...record, ...updateData } : record
          )
        );
        
        setEditingId(null);
        setEditedRowData(null);
        
        showSuccessToast("Advance payment record updated successfully!");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      showErrorToast("Failed to save changes!");
    } finally {
      setIsProcessing(false);
    }
  };

  // Save inline edits for periodic payments
  const handlePeriodicSaveInlineEdit = async () => {
    if (!editedPeriodicRowData) return;
    
    try {
      setIsProcessing(true);
      
      const updateData = {
        invoice_no: editedPeriodicRowData.invoice_no,
        agreement_id: editedPeriodicRowData.agreement_id,
        amount: editedPeriodicRowData.amount,
        payment_date: editedPeriodicRowData.payment_date,
        payment_type: editedPeriodicRowData.payment_type,
        bank_name: editedPeriodicRowData.bank_name,
        reference_no: editedPeriodicRowData.reference_no,
        description: editedPeriodicRowData.description,
        if_late_penality_interest_amount: editedPeriodicRowData.if_late_penalty_interest_amount,
        recieved_by: editedPeriodicRowData.recieved_by,
        payment_period: editedPeriodicRowData.payment_period,
        arrear_skipped_amount: editedPeriodicRowData.arrear_skipped_amount,
        arrear_skipping_description: editedPeriodicRowData.arrear_skipping_description,
        status: editedPeriodicRowData.status
      };
      
      console.log("Updating periodic payment record:", periodicEditingInvoiceNo, updateData);
      
      // Use invoice_no in the API endpoint
      const result = await UpdateAndGetResponse(`finances/periodic-payments/${periodicEditingInvoiceNo}`, updateData);
      
      if (result) {
        setPeriodicSearchResults(prevResults => 
          prevResults.map(record => 
            record.invoice_no === periodicEditingInvoiceNo ? { 
              ...record, 
              ...updateData,
              amount_formatted: formatCurrency(updateData.amount),
              payment_date_formatted: formatDate(updateData.payment_date),
              late_interest_formatted: formatCurrency(updateData.if_late_penality_interest_amount),
              arrear_skipped_formatted: formatCurrency(updateData.arrear_skipped_amount)
            } : record
          )
        );
        
        setPeriodicPaymentData(prevData => 
          prevData.map(record => 
            record.invoice_no === periodicEditingInvoiceNo ? { 
              ...record, 
              ...updateData,
              amount_formatted: formatCurrency(updateData.amount),
              payment_date_formatted: formatDate(updateData.payment_date),
              late_interest_formatted: formatCurrency(updateData.if_late_penality_interest_amount),
              arrear_skipped_formatted: formatCurrency(updateData.arrear_skipped_amount)
            } : record
          )
        );
        
        setPeriodicEditingInvoiceNo(null);
        setEditedPeriodicRowData(null);
        
        showSuccessToast("Periodic payment record updated successfully!");
      }
    } catch (error) {
      console.error("Error saving periodic record:", error);
      showErrorToast("Failed to save changes to periodic payment!");
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel inline editing
  const handleCancelInlineEdit = () => {
    setEditingId(null);
    setEditedRowData(null);
  };

  const handlePeriodicCancelInlineEdit = () => {
    setPeriodicEditingInvoiceNo(null);
    setEditedPeriodicRowData(null);
  };

  // Handle field changes in inline editing
  const handleFieldChange = (fieldName, value) => {
    setEditedRowData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handlePeriodicFieldChange = (fieldName, value) => {
    setEditedPeriodicRowData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // ==================== DELETE FUNCTIONS ====================

  // Delete advance payment record
  const handleDeleteRecord = async (recordId) => {
    if (window.confirm("Are you sure you want to delete this advance payment record?")) {
      try {
        setIsProcessing(true);
        
        await DeleteAndGetResponse(`finances/advance-payments/${recordId}`);
        
        setPaymentData(prevData => prevData.filter(item => item.id !== recordId));
        setSearchResults(prevData => prevData.filter(item => item.id !== recordId));
        setTotalRecords(prev => prev - 1);
        
        if (editingId === recordId) {
          setEditingId(null);
          setEditedRowData(null);
        }
        
        showSuccessToast("Advance payment record deleted successfully!");
      } catch (error) {
        console.error("Error deleting record:", error);
        showErrorToast("Failed to delete advance payment record!");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // Delete periodic payment record
  const handlePeriodicDeleteRecord = async (record) => {
    if (window.confirm("Are you sure you want to delete this periodic payment record?")) {
      try {
        setIsProcessing(true);
        
        // Use invoice_no in the API endpoint
        await DeleteAndGetResponse(`finances/periodic-payments/${record.invoice_no}`);
        
        setPeriodicPaymentData(prevData => prevData.filter(item => item.invoice_no !== record.invoice_no));
        setPeriodicSearchResults(prevData => prevData.filter(item => item.invoice_no !== record.invoice_no));
        setPeriodicTotalRecords(prev => prev - 1);
        
        if (periodicEditingInvoiceNo === record.invoice_no) {
          setPeriodicEditingInvoiceNo(null);
          setEditedPeriodicRowData(null);
        }
        
        showSuccessToast("Periodic payment record deleted successfully!");
      } catch (error) {
        console.error("Error deleting periodic record:", error);
        showErrorToast("Failed to delete periodic payment record!");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // ==================== VIEW FUNCTIONS ====================

  // View advance record details
  const handleViewRecord = (record) => {
    setSelectedRow(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRow(null);
  };

  // View periodic record details
  const handlePeriodicViewRecord = (record) => {
    setSelectedPeriodicRow(record);
    setPeriodicViewDialogOpen(true);
  };

  const handleClosePeriodicViewDialog = () => {
    setPeriodicViewDialogOpen(false);
    setSelectedPeriodicRow(null);
  };

  // ==================== ACTION MENU FUNCTIONS ====================

  // Action menu handlers for advance payments
  const handleActionMenuOpen = (event, row) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  // Action menu handlers for periodic payments
  const handlePeriodicActionMenuOpen = (event, row) => {
    setPeriodicActionMenuAnchorEl(event.currentTarget);
    setSelectedPeriodicRow(row);
  };

  const handlePeriodicActionMenuClose = () => {
    setPeriodicActionMenuAnchorEl(null);
  };

  // Action handlers for MoreVert menu (advance)
  const handleApproveClick = () => {
    setApprovalDialogOpen(true);
    handleActionMenuClose();
  };

  const handleDeclineClick = () => {
    setDeclineDialogOpen(true);
    handleActionMenuClose();
  };

  const handleRequestClick = () => {
    setRequestDialogOpen(true);
    handleActionMenuClose();
  };

  const handleViewDetails = (row) => {
    handleViewRecord(row);
    handleActionMenuClose();
  };

  // Action handlers for MoreVert menu (periodic)
  const handlePeriodicApproveClick = () => {
    setPeriodicApprovalDialogOpen(true);
    handlePeriodicActionMenuClose();
  };

  const handlePeriodicDeclineClick = () => {
    setPeriodicDeclineDialogOpen(true);
    handlePeriodicActionMenuClose();
  };

  const handlePeriodicRequestClick = () => {
    setPeriodicRequestDialogOpen(true);
    handlePeriodicActionMenuClose();
  };

  const handlePeriodicViewDetails = (row) => {
    handlePeriodicViewRecord(row);
    handlePeriodicActionMenuClose();
  };

  // ==================== STATUS UPDATE FUNCTIONS ====================

  // API call to update status for advance payments
  const updatePaymentStatus = async (paymentId, newStatus, remarks = "") => {
    setIsProcessing(true);
    try {
      const updateData = {
        id: paymentId,
        status: newStatus,
        remarks: remarks,
        approved_by: decodedUser?.username || "System"
      };
      
      console.log("APPROVAL DATA", updateData)
      const result = await UpdateAndGetResponse(`finances/advancepaymentsapprove/${paymentId}`, updateData);
      
      if (result) {
        setPaymentData(prevData =>
          prevData.map(item =>
            item.id === paymentId
              ? { ...item, status: newStatus, remarks: remarks }
              : item
          )
        );
        
        setSearchResults(prevResults => 
          prevResults.map(item =>
            item.id === paymentId
              ? { ...item, status: newStatus, remarks: remarks }
              : item
          )
        );
        
        showSuccessToast(`Advance payment ${newStatus} successfully!`);
        return true;
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showErrorToast(`Failed to ${newStatus} advance payment`);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // API call to update status for periodic payments
const updatePeriodicPaymentStatus = async (invoiceNo, newStatus, remarks = "") => {
  setIsProcessing(true);
  try {
    // Send ONLY the status in the request body
    const updateData = {
      status: newStatus
      // REMOVED: remarks, approved_by, approval_date
    };
    
    console.log("Sending only status:", updateData);
    
    let endpoint;
    switch(newStatus.toLowerCase()) {
      case "approved":
        endpoint = `finances/periodicpaymentsapprove/${invoiceNo}`;
        break;
      case "declined":
        endpoint = `finances/periodicpaymentsdecline/${invoiceNo}`;
        break;
      case "requested":
        endpoint = `finances/periodicpaymentsrequest-info/${invoiceNo}`;
        break;
      default:
        endpoint = `finances/periodic-payments/${invoiceNo}`;
    }
    
    console.log("ENDPOINT:", endpoint);
    
    const result = await UpdateAndGetResponse(endpoint, updateData);
    
    if (result) {
      // Update local state
      setPeriodicPaymentData(prevData =>
        prevData.map(item =>
          item.invoice_no === invoiceNo
            ? { ...item, status: newStatus }
            : item
        )
      );
      
      setPeriodicSearchResults(prevResults => 
        prevResults.map(item =>
          item.invoice_no === invoiceNo
            ? { ...item, status: newStatus }
            : item
        )
      );
      
      showSuccessToast(`Periodic payment ${newStatus} successfully!`);
      return true;
    }
  } catch (error) {
    console.error("Error updating periodic payment status:", error);
    showErrorToast(`Failed to ${newStatus} periodic payment`);
    return false;
  } finally {
    setIsProcessing(false);
  }
};
  // ==================== DIALOG CONFIRM FUNCTIONS ====================

  // Dialog handlers for advance payments
  const handleApproveConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setApprovalDialogOpen(false);
      return;
    }
       
    const success = await updatePaymentStatus(selectedRow.id, "Approved", remarks);
    if (success) {
      setApprovalDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
      fetchAdvancePayments();
    }
  };

  const handleDeclineConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setDeclineDialogOpen(false);
      return;
    }
    
    const success = await updatePaymentStatus(selectedRow.id, "Declined", remarks);
    if (success) {
      setDeclineDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
    }
  };

  const handleRequestConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setRequestDialogOpen(false);
      return;
    }
    
    const success = await updatePaymentStatus(selectedRow.id, "Requested", remarks);
    if (success) {
      setRequestDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
    }
  };

  // Dialog handlers for periodic payments
  const handlePeriodicApproveConfirm = async () => {
    if (!selectedPeriodicRow) {
      showErrorToast("No record selected!");
      setPeriodicApprovalDialogOpen(false);
      return;
    }
      
    const success = await updatePeriodicPaymentStatus(selectedPeriodicRow.invoice_no, "Approved", remarks);
    if (success) {
      setPeriodicApprovalDialogOpen(false);
      setRemarks("");
      setSelectedPeriodicRow(null);
      fetchPeriodicPayments();
    }
  };

  const handlePeriodicDeclineConfirm = async () => {
    if (!selectedPeriodicRow) {
      showErrorToast("No record selected!");
      setPeriodicDeclineDialogOpen(false);
      return;
    }
    
    const success = await updatePeriodicPaymentStatus(selectedPeriodicRow.invoice_no, "declined", remarks);
    if (success) {
      setPeriodicDeclineDialogOpen(false);
      setRemarks("");
      setSelectedPeriodicRow(null);
    }
  };

  const handlePeriodicRequestConfirm = async () => {
    if (!selectedPeriodicRow) {
      showErrorToast("No record selected!");
      setPeriodicRequestDialogOpen(false);
      return;
    }
    
    const success = await updatePeriodicPaymentStatus(selectedPeriodicRow.invoice_no, "requested", remarks);
    if (success) {
      setPeriodicRequestDialogOpen(false);
      setRemarks("");
      setSelectedPeriodicRow(null);
    }
  };

  // ==================== PAGINATION FUNCTIONS ====================

  // Handle pagination for advance payments
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle pagination for periodic payments
  const handlePeriodicChangePage = (event, newPage) => {
    setPeriodicPage(newPage);
  };

  const handlePeriodicChangeRowsPerPage = (event) => {
    setPeriodicRowsPerPage(parseInt(event.target.value, 10));
    setPeriodicPage(0);
  };

  // ==================== REFRESH FUNCTIONS ====================

  // Refresh lists
  const handleRefresh = () => {
    fetchAdvancePayments();
    showWarningToast("Refreshing advance payment records...");
  };

  const handlePeriodicRefresh = () => {
    fetchPeriodicPayments();
    showWarningToast("Refreshing periodic payment records...");
  };

  // ==================== PAGINATED DATA ====================

  // Calculate paginated data
  const paginatedData = searchResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const periodicPaginatedData = periodicSearchResults.slice(
    periodicPage * periodicRowsPerPage,
    periodicPage * periodicRowsPerPage + periodicRowsPerPage
  );

  // ==================== RENDER ====================

  return (
    <>
    {/* Periodic Payment Section */}
    <Container maxWidth="xl">
      {/* Toast Notification */}
      {toastMessage.message && (
        <IPDCToastMessageResult 
          message={toastMessage.message} 
          type={toastMessage.type} 
        />
      )}

      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Periodic Payment Approvals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve periodic payment requests
        </Typography>
      </Box>
          
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by Invoice No, Agreement ID, Reference No, or Description"
              value={periodicSearchQuery}
              onChange={(e) => setPeriodicSearchQuery(e.target.value)}
              onKeyPress={handlePeriodicSearchKeyPress}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              disabled={periodicLoading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handlePeriodicSearch}
              disabled={isPeriodicSearching || periodicLoading}
              startIcon={<SearchIcon />}
              sx={{
                bgcolor: '#9e9e9e',
                '&:hover': {
                  bgcolor: '#757575',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e0e0e0',
                }
              }}
            >
              {isPeriodicSearching ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handlePeriodicRefresh}
              startIcon={<RefreshIcon />}
              disabled={periodicLoading}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#115293',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                '&.Mui-disabled': {
                  borderColor: '#bdbdbd',
                }
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        
        {/* Filter chips and status */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={(e) => setPeriodicFilterAnchorEl(e.currentTarget)}
            variant={periodicStatusFilter !== "all" ? "contained" : "outlined"}
            color="primary"
            disabled={periodicLoading}
          >
            Status Filter
          </Button>
          
          {periodicStatusFilter !== "all" && (
            <Chip
              label={`Status: ${periodicStatusFilter}`}
              onDelete={() => applyPeriodicStatusFilter("all")}
              size="small"
              color="primary"
            />
          )}
          
          {(periodicSearchQuery || periodicStatusFilter !== "all") && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handlePeriodicClearFilters}
              variant="outlined"
              disabled={periodicLoading}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Status filter menu */}
      <Menu
        anchorEl={periodicFilterAnchorEl}
        open={Boolean(periodicFilterAnchorEl)}
        onClose={() => setPeriodicFilterAnchorEl(null)}
      >
        <MenuItem onClick={() => applyPeriodicStatusFilter("all")}>
          All Status
        </MenuItem>
        <MenuItem onClick={() => applyPeriodicStatusFilter("Pending")}>
          <Chip label="PENDING" color="warning" size="small" sx={{ mr: 1 }} />
          Pending
        </MenuItem>
        <MenuItem onClick={() => applyPeriodicStatusFilter("Approved")}>
          <Chip label="APPROVED" color="success" size="small" sx={{ mr: 1 }} />
          Approved
        </MenuItem>
        <MenuItem onClick={() => applyPeriodicStatusFilter("Declined")}>
          <Chip label="DECLINED" color="error" size="small" sx={{ mr: 1 }} />
          Declined
        </MenuItem>
      </Menu>

      {/* Action menu for Periodic Payments */}
      <Menu
        anchorEl={periodicActionMenuAnchorEl}
        open={Boolean(periodicActionMenuAnchorEl)}
        onClose={handlePeriodicActionMenuClose}
      >
        <MenuItem onClick={() => handlePeriodicViewDetails(selectedPeriodicRow)}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handlePeriodicApproveClick} disabled={selectedPeriodicRow?.status === "approved"}>
          <CheckCircleIcon sx={{ mr: 1, fontSize: 20, color: 'success.main' }} />
          Approve
        </MenuItem>
        <MenuItem onClick={handlePeriodicDeclineClick} disabled={selectedPeriodicRow?.status === "declined"}>
          <CancelIcon sx={{ mr: 1, fontSize: 20, color: 'error.main' }} />
          Decline
        </MenuItem>
      </Menu>

      {/* Results Section for Periodic Payments */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Agreement ID</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Type</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell>Reference No</TableCell>
                <TableCell>Payment Period</TableCell>
                <TableCell>Received By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periodicLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading periodic payment records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : periodicSearchResults.length === 0 ? ( 
                <TableRow>
                  {console.log("DataSet:", editedPeriodicRowData)}
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {periodicSearchQuery || periodicStatusFilter !== "all" 
                        ? 'No records found for your search criteria. Try a different search term.' 
                        : 'No periodic payment records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                periodicPaginatedData.map((record) => (
                  <TableRow 
                    key={record.invoice_no}
                    hover
                    sx={{ 
                      backgroundColor: periodicEditingInvoiceNo === record.invoice_no ? 'action.selected' : 'inherit',
                      '&:hover': {
                        backgroundColor: periodicEditingInvoiceNo === record.invoice_no ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    {/* Invoice No - Editable */}
                    <EditableTableCell
                      record={record}
                      field="invoice_no"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.invoice_no : record.invoice_no}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Agreement ID - Non-editable */}
                    <TableCell>{record.agreement_id}</TableCell>
                    
                    {/* Payment Date - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_date"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.payment_date : record.payment_date}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="date"
                    />
                    
                    {/* Amount - Editable */}
                    <EditableTableCell
                      record={record}
                      field="amount"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.amount : record.amount}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="number"
                    />
                    
                    {/* Payment Type - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_type"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.payment_type : record.payment_type}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="paymentType"
                    />
                    
                    {/* Bank Name - Editable */}
                    <EditableTableCell
                      record={record}
                      field="bank_name"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.bank_name : record.bank_name}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Reference No - Editable */}
                    <EditableTableCell
                      record={record}
                      field="reference_no"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.reference_no : record.reference_no}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Payment Period - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_period"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.payment_period : record.payment_period}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Received By - Editable */}
                    <EditableTableCell
                      record={record}
                      field="recieved_by"
                      value={periodicEditingInvoiceNo === record.invoice_no ? editedPeriodicRowData?.recieved_by : record.recieved_by}
                      isEditing={periodicEditingInvoiceNo === record.invoice_no}
                      onFieldChange={handlePeriodicFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Status - Display */}
                    <TableCell>
                      {record.status}
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        {periodicEditingInvoiceNo === record.invoice_no ? (
                          <>
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={handlePeriodicSaveInlineEdit}
                              title="Save Changes"
                              disabled={isProcessing}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={handlePeriodicCancelInlineEdit}
                              title="Cancel Edit"
                              disabled={isProcessing}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handlePeriodicViewRecord(record)}
                              title="View Details"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            
                            <IconButton 
                              size="small" 
                              onClick={(e) => handlePeriodicActionMenuOpen(e, record)}
                              title="More Actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={periodicTotalRecords}
          rowsPerPage={periodicRowsPerPage}
          page={periodicPage}
          onPageChange={handlePeriodicChangePage}
          onRowsPerPageChange={handlePeriodicChangeRowsPerPage}
        />
      </Paper>

      {/* View Periodic Record Dialog */}
      <Dialog 
        open={periodicViewDialogOpen} 
        onClose={handleClosePeriodicViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Periodic Payment Details - Invoice: {selectedPeriodicRow?.invoice_no}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPeriodicRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Invoice No</Typography>
                <Typography variant="body1">{selectedPeriodicRow.invoice_no || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Agreement ID</Typography>
                <Typography variant="body1">{selectedPeriodicRow.agreement_id}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Date</Typography>
                <Typography variant="body1">{selectedPeriodicRow.payment_date_formatted}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedPeriodicRow.amount}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Type</Typography>
                <Typography variant="body1">{selectedPeriodicRow.payment_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Bank Name</Typography>
                <Typography variant="body1">{selectedPeriodicRow.bank_name || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Reference No</Typography>
                <Typography variant="body1">{selectedPeriodicRow.reference_no || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedPeriodicRow.description || 'No description provided'}
                </Typography>
              </Grid>
              
              {selectedPeriodicRow.if_late_penalty_interest_amount > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Late Penalty Interest</Typography>
                  <Typography variant="body1" color="error">
                    {formatCurrency(selectedPeriodicRow.if_late_penalty_interest_amount)}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Received By</Typography>
                <Typography variant="body1">{selectedPeriodicRow.recieved_by || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Period</Typography>
                <Typography variant="body1">{selectedPeriodicRow.payment_period || 'N/A'}</Typography>
              </Grid>
              
              {selectedPeriodicRow.arrear_skipped_amount > 0 && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Arrear Skipped Amount</Typography>
                    <Typography variant="body1" color="warning.main">
                      {formatCurrency(selectedPeriodicRow.arrear_skipped_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Arrear Skipping Description</Typography>
                    <Typography variant="body1">{selectedPeriodicRow.arrear_skipping_description || 'N/A'}</Typography>
                  </Grid>
                </>
              )}
              
              {selectedPeriodicRow.references_path && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Attachments</Typography>
                  <Button
                    startIcon={<AttachFileIcon />}
                    variant="outlined"
                    size="small"
                    href={selectedPeriodicRow.references_path}
                    target="_blank"
                  >
                    View Attachments
                  </Button>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={(selectedPeriodicRow.status || "pending").toUpperCase()} 
                  color={getStatusColor(selectedPeriodicRow.status || "pending")}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePeriodicViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Periodic Payment Approve Dialog */}
      <Dialog 
        open={periodicApprovalDialogOpen} 
        onClose={() => {
          setPeriodicApprovalDialogOpen(false);
          setSelectedPeriodicRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography>Approve Periodic Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPeriodicRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Invoice No:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedPeriodicRow.invoice_no || 'N/A'}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Agreement ID:
              </Typography>
              <Typography variant="body1" fontWeight="medium">{selectedPeriodicRow.agreement_id}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Amount:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {selectedPeriodicRow.amount}
              </Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to approve this periodic payment?
          </Typography>
          
          <TextField
            label="Approval Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any remarks or comments..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setPeriodicApprovalDialogOpen(false);
              setSelectedPeriodicRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePeriodicApproveConfirm} 
            variant="contained" 
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Periodic Payment Decline Dialog */}
      <Dialog 
        open={periodicDeclineDialogOpen} 
        onClose={() => {
          setPeriodicDeclineDialogOpen(false);
          setSelectedPeriodicRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: 'error.main' }} />
            <Typography>Decline Periodic Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPeriodicRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Invoice No:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedPeriodicRow.invoice_no || 'N/A'}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Agreement ID:
              </Typography>
              <Typography variant="body1" fontWeight="medium">{selectedPeriodicRow.agreement_id}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Amount:
              </Typography>
              <Typography variant="body1" fontWeight="bold">{selectedPeriodicRow.amount}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to decline this periodic payment?
          </Typography>
          
          <TextField
            label="Reason for Decline"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Please provide reason for declining..."
            required
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setPeriodicDeclineDialogOpen(false);
              setSelectedPeriodicRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePeriodicDeclineConfirm} 
            variant="contained" 
            color="error"
            startIcon={<CancelIcon />}
            disabled={isProcessing || !remarks.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Decline"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>

    <hr className="border border-dark border-3 mt-5 mb-3 opacity-100"/>

    {/* Advance Payment Section */}
    <Container maxWidth="xl">
      {/* Toast Notification */}
      {toastMessage.message && (
        <IPDCToastMessageResult 
          message={toastMessage.message} 
          type={toastMessage.type} 
        />
      )}

      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Advance Payment Approvals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve advance payment requests
        </Typography>
      </Box>
          
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by Agreement ID, Reference No, or Remarks"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching || loading}
              startIcon={<SearchIcon />}
              sx={{
                bgcolor: '#9e9e9e',
                '&:hover': {
                  bgcolor: '#757575',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e0e0e0',
                }
              }}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#115293',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                '&.Mui-disabled': {
                  borderColor: '#bdbdbd',
                }
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        
        {/* Filter chips and status */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            variant={statusFilter !== "all" ? "contained" : "outlined"}
            color="primary"
            disabled={loading}
          >
            Status Filter
          </Button>
          
          {statusFilter !== "all" && (
            <Chip
              label={`Status: ${statusFilter}`}
              onDelete={() => applyStatusFilter("all")}
              size="small"
              color="primary"
            />
          )}
          
          {(searchQuery || statusFilter !== "all") && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              variant="outlined"
              disabled={loading}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Status filter menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem onClick={() => applyStatusFilter("all")}>
          All Status
        </MenuItem>
        <MenuItem onClick={() => applyStatusFilter("pending")}>
          <Chip label="PENDING" color="warning" size="small" sx={{ mr: 1 }} />
          Pending
        </MenuItem>
        <MenuItem onClick={() => applyStatusFilter("approved")}>
          <Chip label="APPROVED" color="success" size="small" sx={{ mr: 1 }} />
          Approved
        </MenuItem>
        <MenuItem onClick={() => applyStatusFilter("declined")}>
          <Chip label="DECLINED" color="error" size="small" sx={{ mr: 1 }} />
          Declined
        </MenuItem>
      </Menu>

      {/* Action menu for Advance Payments */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedRow)}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleApproveClick} disabled={selectedRow?.status === "approved"}>
          <CheckCircleIcon sx={{ mr: 1, fontSize: 20, color: 'success.main' }} />
          Approve
        </MenuItem>
        <MenuItem onClick={handleDeclineClick} disabled={selectedRow?.status === "declined"}>
          <CancelIcon sx={{ mr: 1, fontSize: 20, color: 'error.main' }} />
          Decline
        </MenuItem>
        
      </Menu>

      {/* Results Section for Advance Payments */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Agreement ID</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Reference No</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading advance payment records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : searchResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery || statusFilter !== "all" 
                        ? 'No records found for your search criteria. Try a different search term.' 
                        : 'No advance payment records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((record) => (
                  <TableRow 
                    key={record.id}
                    hover
                    sx={{ 
                      backgroundColor: editingId === record.id ? 'action.selected' : 'inherit',
                      '&:hover': {
                        backgroundColor: editingId === record.id ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.agreement_id}</TableCell>
                    
                    {/* Payment Date - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_date"
                      value={editingId === record.id ? editedRowData?.payment_date : record.payment_date}
                      isEditing={editingId === record.id}
                      onFieldChange={handleFieldChange}
                      fieldType="date"
                    />
                    
                    {/* Amount - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_amount"
                      value={editingId === record.id ? editedRowData?.payment_amount : record.payment_amount}
                      isEditing={editingId === record.id}
                      onFieldChange={handleFieldChange}
                      fieldType="number"
                    />
                    
                    {/* Payment Mode - Editable */}
                    <EditableTableCell
                      record={record}
                      field="payment_mode"
                      value={editingId === record.id ? editedRowData?.payment_mode : record.payment_mode}
                      isEditing={editingId === record.id}
                      onFieldChange={handleFieldChange}
                      fieldType="paymentMode"
                    />
                    
                    {/* Reference No - Editable */}
                    <EditableTableCell
                      record={record}
                      field="reference_no"
                      value={editingId === record.id ? editedRowData?.reference_no : record.reference_no}
                      isEditing={editingId === record.id}
                      onFieldChange={handleFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Remarks - Editable */}
                    <EditableTableCell
                      record={record}
                      field="remarks"
                      value={editingId === record.id ? editedRowData?.remarks : record.remarks}
                      isEditing={editingId === record.id}
                      onFieldChange={handleFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Status - Display */}
                    <TableCell>
                      {editingId === record.id ? (
                        <Select
                          size="small"
                          value={editedRowData?.status || 'pending'}
                          onChange={(e) => handleFieldChange("status", e.target.value)}
                          sx={{ width: '120px' }}
                        >
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="declined">Declined</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                        </Select>
                      ) : (
                        record.status
                      )}
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        {editingId === record.id ? (
                          <>
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={handleSaveInlineEdit}
                              title="Save Changes"
                              disabled={isProcessing}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={handleCancelInlineEdit}
                              title="Cancel Edit"
                              disabled={isProcessing}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleViewRecord(record)}
                              title="View Details"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleActionMenuOpen(e, record)}
                              title="More Actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Advance Record Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Advance Payment Details
          
        </DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">ID</Typography>
                <Typography variant="body1">{selectedRow.id}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Agreement ID</Typography>
                <Typography variant="body1">{selectedRow.agreement_id}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Date</Typography>
                <Typography variant="body1">{selectedRow.payment_date_formatted}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedRow.payment_amount_formatted}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Mode</Typography>
                <Typography variant="body1">{selectedRow.payment_mode || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Reference No</Typography>
                <Typography variant="body1">{selectedRow.reference_no || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Requested By</Typography>
                <Typography variant="body1">{selectedRow.requested_by || 'System'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Requested Date</Typography>
                <Typography variant="body1">{selectedRow.requested_date_formatted}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Remarks</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRow.remarks || 'No remarks provided'}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                {selectedRow.status}
                
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          
        </DialogActions>
      </Dialog>

      {/* Advance Payment Approve Dialog */}
      <Dialog 
        open={approvalDialogOpen} 
        onClose={() => {
          setApprovalDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography>Approve Advance Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Agreement ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.agreement_id}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Amount:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {selectedRow.payment_amount_formatted}
              </Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to approve this advance payment?
          </Typography>
          
          <TextField
            label="Approval Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any remarks or comments..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setApprovalDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleApproveConfirm} 
            variant="contained" 
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advance Payment Decline Dialog */}
      <Dialog 
        open={declineDialogOpen} 
        onClose={() => {
          setDeclineDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: 'error.main' }} />
            <Typography>Decline Advance Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Agreement ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.agreement_id}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Amount:
              </Typography>
              <Typography variant="body1" fontWeight="bold">{selectedRow.payment_amount_formatted}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to decline this advance payment?
          </Typography>
          
          <TextField
            label="Reason for Decline"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Please provide reason for declining..."
            required
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setDeclineDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeclineConfirm} 
            variant="contained" 
            color="error"
            startIcon={<CancelIcon />}
            disabled={isProcessing || !remarks.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Decline"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request More Info Dialog */}
      <Dialog 
        open={requestDialogOpen} 
        onClose={() => {
          setRequestDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'warning.main' }} />
            <Typography>Request More Information</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Agreement ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.agreement_id}</Typography>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Amount:
              </Typography>
              <Typography variant="body1" fontWeight="bold">{selectedRow.payment_amount_formatted}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            What additional information do you need?
          </Typography>
          
          <TextField
            label="Request Details"
            fullWidth
            multiline
            rows={4}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Specify what additional information or documents are needed..."
            required
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setRequestDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRequestConfirm} 
            variant="contained" 
            color="warning"
            startIcon={<InfoIcon />}
            disabled={isProcessing || !remarks.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
}